import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { setSessionExpiredHandler } from '../services/api';

/* ── Types ───────────────────────────────────────────────────────────────── */

export interface NexoraUser {
  id: string;
  fullName: string;
  email: string;
  phone?: string | null;
  role: 'SUPER_ADMIN' | 'SOCIETY_ADMIN' | 'STAFF' | 'GUARD' | 'TREASURER' | 'VIEW_ONLY_ADMIN' | 'THEME_MANAGER' | 'ANALYST' | 'VIEWER' | 'RESIDENT' | 'TENANT';
  societyId: string | null;
  societyName: string | null;
  societyCode?: string | null;
  lastLoginAt: string | null;
}

interface AuthState {
  user: NexoraUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

interface AuthContextValue extends AuthState {
  login: (accessToken: string, refreshToken: string, user: NexoraUser) => void;
  logout: () => Promise<void>;
  updateUser: (user: NexoraUser) => void;
}

/* ── Token storage ───────────────────────────────────────────────────────── */

const ACCESS_TOKEN_KEY = 'nexora_access_token';
const REFRESH_TOKEN_KEY = 'nexora_refresh_token';
const USER_KEY = 'nexora_user';

export const getAccessToken = () => localStorage.getItem(ACCESS_TOKEN_KEY);
export const getRefreshToken = () => localStorage.getItem(REFRESH_TOKEN_KEY);
export const setTokens = (a: string, r: string) => {
  localStorage.setItem(ACCESS_TOKEN_KEY, a);
  localStorage.setItem(REFRESH_TOKEN_KEY, r);
};
export const clearTokens = () => {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
};

/* ── Context ─────────────────────────────────────────────────────────────── */

export const AuthContext = createContext<AuthContextValue>({
  user: null,
  isLoading: true,
  isAuthenticated: false,
  login: () => {},
  logout: async () => {},
  updateUser: () => {},
});

export const useAuth = () => useContext(AuthContext);

/* ── Provider ────────────────────────────────────────────────────────────── */

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<NexoraUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  /* ── Restore session on mount ── */
  useEffect(() => {
    const token = getAccessToken();
    const stored = localStorage.getItem(USER_KEY);
    if (token && stored) {
      try {
        setUser(JSON.parse(stored) as NexoraUser);
      } catch {
        clearTokens();
      }
    }
    setIsLoading(false);

    setSessionExpiredHandler(() => {
      clearTokens();
      setUser(null);
      navigate('/login');
    });
  }, [navigate]);

  /* ── Cross-tab session sync ──────────────────────────────────────────────
   * Tokens live in localStorage, which is shared by every tab of this origin.
   * If a different account logs in (or logs out) in another tab, this tab's
   * in-memory `user` still reflects the old account while its token has
   * silently become the new account's — every subsequent request then either
   * 401s (wrong scope) or "succeeds" against the wrong account. The `storage`
   * event only fires in tabs OTHER than the one that made the change, so this
   * lets a stale tab detect the swap and reload to pick up the current session
   * cleanly instead of limping along on a foreign token.
   */
  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key !== ACCESS_TOKEN_KEY && e.key !== USER_KEY) return;

      const token = getAccessToken();
      const stored = localStorage.getItem(USER_KEY);
      if (!token || !stored) {
        setUser(null);
        navigate('/login');
        return;
      }
      try {
        const nextUser = JSON.parse(stored) as NexoraUser;
        setUser((prev) => {
          if (prev && prev.id !== nextUser.id) {
            // Different account signed in elsewhere — this tab's state (and any
            // in-flight requests) belong to the old session. Reload rather than
            // silently swap identities under the user.
            window.location.reload();
            return prev;
          }
          return nextUser;
        });
      } catch {
        /* ignore malformed value */
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [navigate]);

  const login = useCallback(
    (accessToken: string, refreshToken: string, authUser: NexoraUser) => {
      setTokens(accessToken, refreshToken);
      localStorage.setItem(USER_KEY, JSON.stringify(authUser));
      setUser(authUser);
    },
    [],
  );

  const logout = useCallback(async () => {
    try {
      await api.post('/api/v1/auth/logout');
    } catch { /* always proceed */ }
    clearTokens();
    setUser(null);
    navigate('/login');
  }, [navigate]);

  const updateUser = useCallback((authUser: NexoraUser) => {
    localStorage.setItem(USER_KEY, JSON.stringify(authUser));
    setUser(authUser);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: user !== null,
        login,
        logout,
        updateUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
